import { makeAutoObservable } from 'mobx';
import { EmailMapping } from 'types/emailService';
import { InstalledEnrollmentType } from 'types/enrollmentService';
import { BiGroup } from 'types/groupService';
import { ContactEmail, EmailDelegate } from './emailDelegate';
import { EnrollmentDelegate } from './enrollmentDelegate';

export class RenewalGroupDelegate {
  private _group: BiGroup;
  private _enrollmentDelegates: EnrollmentDelegate[];
  private _emails: EmailMapping[] = [];

  public get group(): BiGroup {
    return this._group;
  }

  public get enrollmentDelegates(): EnrollmentDelegate[] {
    return this._enrollmentDelegates;
  }

  /** Every period of the group reserves the same number of email rows, so in-cell dividers stay aligned */
  public get emailRowsCount(): number {
    return Math.max(1, ...this._enrollmentDelegates.map(({ emailUserTypesCount }) => emailUserTypesCount));
  }

  public getEnrollmentDelegate(periodId: string): EnrollmentDelegate {
    const enrollmentDelegate = this._enrollmentDelegates.find((delegate) => delegate.period.id === periodId);
    if (!enrollmentDelegate) {
      throw new Error(`Enrollment delegate for period ${periodId} not found in the group ${this._group.name}`);
    }

    return enrollmentDelegate;
  }

  public updateEnrollmentPeriod(updatedPeriod: InstalledEnrollmentType): void {
    this.getEnrollmentDelegate(updatedPeriod.id).update(updatedPeriod);
  }

  public removeEnrollment(periodId: string): void {
    this._enrollmentDelegates = this._enrollmentDelegates.filter(
      (enrollmentDelegate) => enrollmentDelegate.period.id !== periodId,
    );
  }

  public updateEmails(emails: EmailMapping[]): void {
    this._emails = emails;
    this._enrollmentDelegates.forEach((enrollmentDelegate) => {
      enrollmentDelegate.emailDelegate.updateMappedEmails(emails);
    });
  }

  public updateGroup(updatedGroup: BiGroup): BiGroup {
    this._group = {
      ...this._group,
      additionalAORs: updatedGroup.additionalAORs ?? this._group.additionalAORs,
      additionalContactInfos: updatedGroup.additionalContactInfos ?? this._group.additionalContactInfos,
      contactInfo: updatedGroup.contactInfo ?? this._group.contactInfo,
      agentOfRecord: updatedGroup.agentOfRecord ?? this._group.agentOfRecord,
    };
    this._enrollmentDelegates.forEach((enrollmentDelegate) => {
      enrollmentDelegate.replaceEmailDelegate(this._createEmailDelegate(enrollmentDelegate.period.id, this._emails));
    });
    return this._group;
  }

  private get _adminEmails(): ContactEmail[] {
    const {
      contactInfo: { primaryEmail },
      additionalContactInfos,
    } = this._group;

    const additionalEmails: ContactEmail[] = additionalContactInfos.flatMap((admin) =>
      admin.primaryEmail ? [{ email: admin.primaryEmail, isAdditional: true }] : [],
    );
    const primaryEmails: ContactEmail[] = primaryEmail ? [{ email: primaryEmail, isAdditional: false }] : [];

    return [...primaryEmails, ...additionalEmails];
  }

  private get _aorEmails(): ContactEmail[] {
    const {
      agentOfRecord: { email: aorEmail },
      additionalAORs,
    } = this._group;

    const additionalEmails: ContactEmail[] = additionalAORs.flatMap(({ email }) =>
      email ? [{ email, isAdditional: true }] : [],
    );
    const primaryEmails: ContactEmail[] = aorEmail ? [{ email: aorEmail, isAdditional: false }] : [];

    return [...primaryEmails, ...additionalEmails];
  }

  private _createEmailDelegate(periodId: string, emails: EmailMapping[]): EmailDelegate {
    return new EmailDelegate({
      periodId,
      mappedEmails: emails,
      adminEmails: this._adminEmails,
      aorEmails: this._aorEmails,
    });
  }

  constructor(group: BiGroup, enrollments: InstalledEnrollmentType[], emails: EmailMapping[]) {
    this._group = group;
    this._emails = emails;
    this._enrollmentDelegates = enrollments.map(
      (period) => new EnrollmentDelegate(period, this._createEmailDelegate(period.id, emails)),
    );
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
